import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Filter, RefreshCw, 
  ChevronRight, Activity, BarChart3, Clock, CheckCircle, XCircle,
  ThumbsUp, ThumbsDown, Sparkles, ArrowLeft
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

function InsightsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [anomalies, setAnomalies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [error, setError] = useState('');
  
  // Filters
  const [filters, setFilters] = useState({
    severity: '',
    direction: '',
    status: '',
    pillar: ''
  });

  useEffect(() => {
    loadInsightsData();
  }, [filters]);

  const loadInsightsData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Build query params
      const params = new URLSearchParams();
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.direction) params.append('direction', filters.direction);
      if (filters.status) params.append('status', filters.status);
      if (filters.pillar) params.append('pillar', filters.pillar);
      
      const [anomaliesRes, summaryRes] = await Promise.all([
        axios.get(`/insights/anomalies?${params.toString()}`),
        axios.get('/insights/summary?days=90')
      ]);
      
      setAnomalies(anomaliesRes.data.anomalies || []);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to load insights:', err);
      setError('Failed to load insights data');
    } finally {
      setLoading(false);
    }
  };

  const runDetection = async () => {
    try {
      setDetecting(true);
      await axios.post('/insights/detect?lookback_days=365');
      await loadInsightsData();
    } catch (err) {
      setError('Failed to run anomaly detection');
    } finally {
      setDetecting(false);
    }
  };

  const loadAnomalyDetail = async (anomaly) => {
    setSelectedAnomaly(anomaly);
    setLoadingAnalysis(true);
    setAnalysis(null);
    setExplanation(null);
    
    try {
      const [analysisRes, explanationRes] = await Promise.all([
        axios.get(`/insights/anomalies/${anomaly.id}/analysis`),
        axios.get(`/insights/anomalies/${anomaly.id}/explanation`)
      ]);
      
      setAnalysis(analysisRes.data);
      setExplanation(explanationRes.data);
    } catch (err) {
      console.error('Failed to load anomaly detail:', err);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const updateAnomalyStatus = async (anomalyId, status) => {
    try {
      await axios.put(`/insights/anomalies/${anomalyId}`, { status });
      await loadInsightsData();
      if (selectedAnomaly?.id === anomalyId) {
        setSelectedAnomaly({ ...selectedAnomaly, status });
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const submitFeedback = async (anomalyId, helpful, falsePositive = false) => {
    try {
      await axios.post(`/insights/anomalies/${anomalyId}/feedback`, {
        helpful,
        false_positive: falsePositive
      });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'false_positive': return <XCircle className="w-4 h-4 text-gray-400" />;
      case 'viewed': return <Activity className="w-4 h-4 text-blue-500" />;
      default: return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length !== 3) return dateStr;
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Waterfall chart data transformation
  const getWaterfallData = () => {
    if (!analysis?.waterfall?.steps) return [];
    return analysis.waterfall.steps.map(step => ({
      name: step.label.length > 15 ? step.label.substring(0, 15) + '...' : step.label,
      fullName: step.label,
      value: step.value,
      cumulative: step.cumulative,
      type: step.type,
      fill: step.type === 'increase' ? '#22c55e' : 
            step.type === 'decrease' ? '#ef4444' : 
            step.type === 'start' ? '#6366f1' : '#8b5cf6'
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard-v2')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="insights-title">
                  Spike & Dip Intelligence
                </h1>
                <p className="text-sm text-gray-500">Anomaly detection and root cause analysis</p>
              </div>
            </div>
            <button
              onClick={runDetection}
              disabled={detecting}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
              data-testid="run-detection-btn"
            >
              <RefreshCw className={`w-4 h-4 ${detecting ? 'animate-spin' : ''}`} />
              {detecting ? 'Detecting...' : 'Run Detection'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <AlertTriangle className="w-4 h-4" />
                Total Anomalies
              </div>
              <p className="text-2xl font-bold text-gray-900">{summary.total_anomalies}</p>
              <p className="text-xs text-gray-500">Last 90 days</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-amber-500 text-sm mb-1">
                <Clock className="w-4 h-4" />
                New/Unread
              </div>
              <p className="text-2xl font-bold text-amber-600">{summary.new_anomalies}</p>
              <p className="text-xs text-gray-500">Require attention</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-green-500 text-sm mb-1">
                <TrendingUp className="w-4 h-4" />
                Spikes
              </div>
              <p className="text-2xl font-bold text-green-600">{summary.spikes}</p>
              <p className="text-xs text-gray-500">Positive changes</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-red-500 text-sm mb-1">
                <TrendingDown className="w-4 h-4" />
                Dips
              </div>
              <p className="text-2xl font-bold text-red-600">{summary.dips}</p>
              <p className="text-xs text-gray-500">Negative changes</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-gray-500">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <select
              value={filters.severity}
              onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              data-testid="severity-filter"
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              value={filters.direction}
              onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              data-testid="direction-filter"
            >
              <option value="">All Directions</option>
              <option value="spike">Spikes Only</option>
              <option value="dip">Dips Only</option>
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              data-testid="status-filter"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="viewed">Viewed</option>
              <option value="resolved">Resolved</option>
              <option value="false_positive">False Positive</option>
            </select>
            <select
              value={filters.pillar}
              onChange={(e) => setFilters({ ...filters, pillar: e.target.value })}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              data-testid="pillar-filter"
            >
              <option value="">All Pillars</option>
              <option value="authenticity">Authenticity</option>
              <option value="accountability">Accountability</option>
              <option value="community">Community</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Anomalies List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Detected Anomalies</h2>
                <p className="text-sm text-gray-500">{anomalies.length} results</p>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading...
                  </div>
                ) : anomalies.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No anomalies detected</p>
                    <p className="text-sm mt-1">Run detection to analyze your data</p>
                  </div>
                ) : (
                  anomalies.map((anomaly) => (
                    <button
                      key={anomaly.id}
                      onClick={() => loadAnomalyDetail(anomaly)}
                      className={`w-full p-4 border-b border-gray-50 hover:bg-gray-50 transition text-left ${
                        selectedAnomaly?.id === anomaly.id ? 'bg-purple-50' : ''
                      }`}
                      data-testid={`anomaly-item-${anomaly.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {anomaly.direction === 'spike' ? (
                            <TrendingUp className="w-5 h-5 text-green-500 flex-shrink-0" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-red-500 flex-shrink-0" />
                          )}
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              {anomaly.scope === 'overall' ? 'Overall Score' : 
                               `${anomaly.pillar?.charAt(0).toUpperCase()}${anomaly.pillar?.slice(1)}`}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(anomaly.anomaly_date)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getSeverityColor(anomaly.severity)}`}>
                            {anomaly.severity}
                          </span>
                          {getStatusIcon(anomaly.status)}
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                        <span className={anomaly.change_percent > 0 ? 'text-green-600' : 'text-red-600'}>
                          {anomaly.change_percent > 0 ? '+' : ''}{anomaly.change_percent?.toFixed(1)}%
                        </span>
                        <span>
                          {anomaly.previous_value?.toFixed(1)} → {anomaly.current_value?.toFixed(1)}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-2">
            {selectedAnomaly ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                {/* Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {selectedAnomaly.direction === 'spike' ? (
                          <TrendingUp className="w-6 h-6 text-green-500" />
                        ) : (
                          <TrendingDown className="w-6 h-6 text-red-500" />
                        )}
                        <h2 className="text-lg font-semibold text-gray-900">
                          {selectedAnomaly.scope === 'overall' ? 'Overall Trust Score' : 
                           `${selectedAnomaly.pillar?.charAt(0).toUpperCase()}${selectedAnomaly.pillar?.slice(1)} Pillar`}
                          {' '}{selectedAnomaly.direction === 'spike' ? 'Spike' : 'Dip'}
                        </h2>
                      </div>
                      <p className="text-sm text-gray-500">
                        Detected on {formatDate(selectedAnomaly.anomaly_date)}
                      </p>
                    </div>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getSeverityColor(selectedAnomaly.severity)}`}>
                      {selectedAnomaly.severity?.toUpperCase()}
                    </span>
                  </div>
                </div>

                {loadingAnalysis ? (
                  <div className="p-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Analyzing anomaly...
                  </div>
                ) : (
                  <>
                    {/* Key Metrics */}
                    <div className="p-4 border-b border-gray-100">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-500">Score Change</p>
                          <p className={`text-xl font-bold ${selectedAnomaly.change_absolute > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedAnomaly.change_absolute > 0 ? '+' : ''}{selectedAnomaly.change_absolute?.toFixed(1)}
                          </p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-500">% Change</p>
                          <p className={`text-xl font-bold ${selectedAnomaly.change_percent > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedAnomaly.change_percent > 0 ? '+' : ''}{selectedAnomaly.change_percent?.toFixed(1)}%
                          </p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-500">Z-Score</p>
                          <p className="text-xl font-bold text-purple-600">
                            {selectedAnomaly.z_score?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* AI Explanation */}
                    {explanation && (
                      <div className="p-4 border-b border-gray-100">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-5 h-5 text-amber-500" />
                          <h3 className="font-semibold text-gray-900">AI Analysis</h3>
                        </div>
                        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4">
                          <p className="text-gray-800 font-medium mb-2">{explanation.headline}</p>
                          <p className="text-gray-600 text-sm">{explanation.summary}</p>
                          
                          {explanation.ai_enhanced?.executive_summary && (
                            <div className="mt-3 pt-3 border-t border-purple-100">
                              <p className="text-sm text-purple-700">{explanation.ai_enhanced.executive_summary}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Suggested Actions */}
                        {explanation.suggested_actions?.length > 0 && (
                          <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Suggested Actions:</h4>
                            <ul className="space-y-1">
                              {explanation.suggested_actions.map((action, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                                  <ChevronRight className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                                  {action}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Waterfall Chart */}
                    {analysis?.waterfall && (
                      <div className="p-4 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-900 mb-4">Score Breakdown (Waterfall)</h3>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={getWaterfallData()} margin={{ top: 10, right: 10, left: 10, bottom: 40 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="name" 
                                tick={{ fontSize: 10 }}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                              />
                              <YAxis tick={{ fontSize: 10 }} />
                              <Tooltip 
                                content={({ payload }) => {
                                  if (payload && payload[0]) {
                                    const data = payload[0].payload;
                                    return (
                                      <div className="bg-white p-2 border border-gray-200 rounded shadow-lg text-sm">
                                        <p className="font-medium">{data.fullName}</p>
                                        <p>Value: {data.value?.toFixed(2)}</p>
                                        <p>Cumulative: {data.cumulative?.toFixed(2)}</p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Bar dataKey="value">
                                {getWaterfallData().map((entry, index) => (
                                  <Cell key={index} fill={entry.fill} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Top Contributors */}
                    {analysis?.contributions?.length > 0 && (
                      <div className="p-4 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-900 mb-3">Top Contributing Factors</h3>
                        <div className="space-y-2">
                          {analysis.contributions.slice(0, 5).map((contrib, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-400 w-4">{idx + 1}</span>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{contrib.metric_name}</p>
                                  <p className="text-xs text-gray-500">{contrib.pillar}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`text-sm font-medium ${contrib.contribution > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {contrib.contribution > 0 ? '+' : ''}{contrib.contribution?.toFixed(2)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {contrib.previous_value?.toFixed(1)} → {contrib.current_value?.toFixed(1)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">Was this helpful?</span>
                          <button 
                            onClick={() => submitFeedback(selectedAnomaly.id, true)}
                            className="p-2 hover:bg-green-50 rounded-lg transition"
                            title="Yes, helpful"
                          >
                            <ThumbsUp className="w-4 h-4 text-gray-400 hover:text-green-500" />
                          </button>
                          <button 
                            onClick={() => submitFeedback(selectedAnomaly.id, false)}
                            className="p-2 hover:bg-red-50 rounded-lg transition"
                            title="Not helpful"
                          >
                            <ThumbsDown className="w-4 h-4 text-gray-400 hover:text-red-500" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedAnomaly.status !== 'resolved' && (
                            <button
                              onClick={() => updateAnomalyStatus(selectedAnomaly.id, 'resolved')}
                              className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                            >
                              Mark Resolved
                            </button>
                          )}
                          {selectedAnomaly.status !== 'false_positive' && (
                            <button
                              onClick={() => {
                                submitFeedback(selectedAnomaly.id, false, true);
                                updateAnomalyStatus(selectedAnomaly.id, 'false_positive');
                              }}
                              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                            >
                              False Positive
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select an Anomaly</h3>
                <p className="text-gray-500">Click on an anomaly from the list to view detailed analysis</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default InsightsPage;
